import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import PencilEdit from '@/assets/icons/pencil-edit.svg';
import { ViewSourcingToggleButton } from '@/components/pages/channels/view-sourcing-toggle-button';
import {
  BANNER_ACTION_BUTTON_CLASSNAME,
  CreateHiringGoalButton,
} from '@/components/pages/channels/create-hiring-goal-button';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { formatShortDate } from '@/utils/date-util';

/**
 * Pure helper that decides whether to show the banner and returns
 * all data needed to render it.
 */
function getHiringBannerData({
  daysLeft,
  actualHires,
  expectedHires,
  endDate,
  intl,
}: {
  daysLeft: number | null;
  actualHires: number | null;
  expectedHires: number | null;
  endDate: string | null;
  intl: IntlShape;
}) {
  const hasHiresInfo = actualHires != null && expectedHires != null;
  const goalEnded = daysLeft === 0;
  const meetsTarget = hasHiresInfo && actualHires >= expectedHires;
  const missesTarget = hasHiresInfo && actualHires < expectedHires;
  const endsTomorrow = daysLeft === 1;

  if (!endDate) return null;

  if (goalEnded && missesTarget) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal ended without reaching target',
        description:
          'Banner title: goal window closed and hire count is below the target',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal ended on {endDate} with {actualHires} out of {expectedHires} hires. Kindly create a new goal if your requirement is still open.',
          description:
            'Banner when the hiring period ended and hire count was below the goal',
        },
        { endDate, actualHires, expectedHires },
      ),
      isSuccess: false,
      endsTomorrow,
    };
  }

  if (goalEnded && meetsTarget) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal completed and target met!.',
        description:
          'Banner title: the goal period ended and hires met or beat the plan',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal ended on {endDate}. Create a new goal to continue tracking sourcing spend.',
          description:
            'Banner when the hiring goal ended and the target number of hires was met',
        },
        { endDate },
      ),
      isSuccess: true,
      endsTomorrow,
    };
  }

  if (endsTomorrow) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal ends in 1 day',
        description:
          'Urgent reminder: the configured hiring period ends tomorrow',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal will end on {endDate}. Kindly consider extending the end date if the requirement is still open.',
          description: 'Warning when the hiring goal has one day remaining',
        },
        { endDate },
      ),
      isSuccess: false,
      endsTomorrow,
    };
  }

  return null;
}

function PerformanceStatusBanner() {
  const intl = useIntl();
  const { opening, hiringGoal, recentPastHiringGoal, hiringGoalEditUrl } =
    useOpeningDetails();
  const { metricsViewMode } = useOpeningContext();

  const goalForBanner =
    metricsViewMode === 'date_range' ? recentPastHiringGoal : hiringGoal;

  if (!goalForBanner) return null;

  const expectedDuration = goalForBanner.duration;
  const elapsedDays = goalForBanner.days_elapsed;
  const daysLeft =
    expectedDuration != null && elapsedDays != null
      ? expectedDuration - elapsedDays
      : null;

  const endDate = goalForBanner.hire_by
    ? formatShortDate(goalForBanner.hire_by)
    : null;
  const actualHires = goalForBanner.hire_stats?.actual;
  const expectedHires = goalForBanner.hire_stats?.expected;

  const bannerData = getHiringBannerData({
    daysLeft,
    actualHires,
    endDate,
    expectedHires,
    intl,
  });

  if (!bannerData) return null;

  const { title, description, isSuccess = false, endsTomorrow } = bannerData;

  const renderActions = () => {
    if (endsTomorrow) {
      return (
        <Button className={BANNER_ACTION_BUTTON_CLASSNAME} asChild>
          <Link to={hiringGoalEditUrl} target="_blank">
            <img src={PencilEdit} alt="" aria-hidden="true" />
            <FormattedMessage
              defaultMessage="Edit Goal"
              description="Banner CTA when goal ends soon: open Hire to adjust dates or targets"
            />
          </Link>
        </Button>
      );
    }

    return (
      <div className="flex shrink-0 items-center gap-3 self-center">
        {!opening.hasActiveHiringGoal && (
          <CreateHiringGoalButton hiringGoalUrl={hiringGoalEditUrl} />
        )}
        <ViewSourcingToggleButton />
      </div>
    );
  };

  return (
    <Banner
      color={isSuccess ? 'green' : 'amber'}
      title={title}
      description={description}
      className="items-start"
      button={renderActions()}
    />
  );
}

export { PerformanceStatusBanner };
